import {
  DecoratorNode,
  type LexicalNode,
  type NodeKey,
  type SerializedLexicalNode,
  type Spread,
} from 'lexical';

import type { NoteEditorBlockDef } from '../../config';

import { markBlockDom } from './blockDom';
import { NoteBlockHost } from './NoteBlockHost';

type SerializedNoteBlockNode = Spread<
  {
    blockId: string;
    def: NoteEditorBlockDef | undefined;
    spaceBefore: boolean;
    aside: boolean;
  },
  SerializedLexicalNode
>;

// A value block hosted in the document: its widget renders the value, so a
// new value changes the widget and never the document. An aside hosts the
// marks and actions of a text block, after the text. The caret steps over
// both as over one character.
export class NoteBlockNode extends DecoratorNode<JSX.Element> {
  __blockId: string;
  __def: NoteEditorBlockDef | undefined;
  __spaceBefore: boolean;
  __aside: boolean;

  static getType(): string {
    return 'note-block';
  }

  static clone(node: NoteBlockNode): NoteBlockNode {
    return new NoteBlockNode(
      node.__blockId,
      node.__def,
      node.__spaceBefore,
      node.__aside,
      node.__key,
    );
  }

  static importJSON(json: SerializedNoteBlockNode): NoteBlockNode {
    return $createNoteBlockNode({
      id: json.blockId,
      def: json.def,
      spaceBefore: json.spaceBefore,
      aside: json.aside,
    });
  }

  constructor(
    blockId = '',
    def: NoteEditorBlockDef | undefined = undefined,
    spaceBefore = false,
    aside = false,
    key?: NodeKey,
  ) {
    super(key);
    this.__blockId = blockId;
    this.__def = def;
    this.__spaceBefore = spaceBefore;
    this.__aside = aside;
  }

  exportJSON(): SerializedNoteBlockNode {
    return {
      ...super.exportJSON(),
      blockId: this.getBlockId(),
      def: this.getDef(),
      spaceBefore: this.getSpaceBefore(),
      aside: this.isAside(),
    };
  }

  createDOM(): HTMLElement {
    const dom = document.createElement('span');
    if (this.__aside) {
      dom.dataset.noteBlockAside = this.__blockId;
      return dom;
    }
    return markBlockDom(dom, this.__blockId, this.__spaceBefore);
  }

  updateDOM(previous: NoteBlockNode, dom: HTMLElement): boolean {
    if (!this.__aside && previous.__spaceBefore !== this.__spaceBefore) {
      markBlockDom(dom, this.__blockId, this.__spaceBefore);
    }
    return false;
  }

  isInline(): boolean {
    return true;
  }

  isKeyboardSelectable(): boolean {
    return false;
  }

  decorate(): JSX.Element {
    return (
      <NoteBlockHost
        blockId={this.__blockId}
        def={this.__def}
        aside={this.__aside}
      />
    );
  }

  getBlockId(): string {
    return this.getLatest().__blockId;
  }

  getDef(): NoteEditorBlockDef | undefined {
    return this.getLatest().__def;
  }

  getSpaceBefore(): boolean {
    return this.getLatest().__spaceBefore;
  }

  isAside(): boolean {
    return this.getLatest().__aside;
  }

  setDef(def: NoteEditorBlockDef | undefined): this {
    const writable = this.getWritable();
    writable.__def = def;
    return writable;
  }

  setSpaceBefore(spaceBefore: boolean): this {
    const writable = this.getWritable();
    writable.__spaceBefore = spaceBefore;
    return writable;
  }
}

export const $createNoteBlockNode = ({
  id,
  def,
  spaceBefore,
  aside = false,
}: {
  id: string;
  def: NoteEditorBlockDef | undefined;
  spaceBefore: boolean;
  aside?: boolean;
}) => new NoteBlockNode(id, def, spaceBefore, aside);

export const $isNoteBlockNode = (
  node: LexicalNode | null | undefined,
): node is NoteBlockNode => node instanceof NoteBlockNode;
